import { NotificationFragment } from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import React from "react";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";
import { ClientNotificationFilterFormValues } from "@components/Forms/ClientNotificationFilterForm/ClientNotificationFilterForm";

import {
  CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS,
  ClientNotificationTableColumns,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "./UI/ClientNotificationsTableColumns";

/**
 * Default filter values for Client Notifications
 */
export const DEFAULT_CLIENT_NOTIFICATION_FILTER_VALUES: ClientNotificationFilterFormValues = {
  notificationType: [],
  applicationNameSearch: "",
};

/**
 * Comprehensive table configuration for Client Notifications
 */
export const ClientNotificationsTableConfig = createTableConfiguration<
  NotificationFragment,
  ClientNotificationTableColumns
>({
  tableId: "client-notifications-v2",
  columns: {
    [ClientNotificationTableColumns.applicationName]: {
      id: ClientNotificationTableColumns.applicationName,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.applicationName
        ],
      fieldPath: "application.resource.productName",
      sortField: "resource__product_name",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown, row: NotificationFragment) => {
        // This will be overridden by custom renderers in the table
        return renderApplicationName(value, row, () => {});
      },
    },
    [ClientNotificationTableColumns.notificationType]: {
      id: ClientNotificationTableColumns.notificationType,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.notificationType
        ],
      fieldPath: "notificationType",
      sortField: "notification_type",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: NotificationFragment) => {
        return renderNotificationType(value, row, () => {});
      },
    },
    [ClientNotificationTableColumns.createdAt]: {
      id: ClientNotificationTableColumns.createdAt,
      label:
        CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[
          ClientNotificationTableColumns.createdAt
        ],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCreatedAt(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = (): ClientNotificationFilterFormValues => ({
  ...DEFAULT_CLIENT_NOTIFICATION_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: ClientNotificationFilterFormValues): boolean => {
  return Boolean(
    (filterValues.notificationType && filterValues.notificationType.length > 0) ||
      (filterValues.applicationNameSearch && filterValues.applicationNameSearch.trim()),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderApplicationName(
  _value: unknown,
  row: NotificationFragment,
  onApplicationDashboardClick: (clientId: string, applicationId: string) => void,
): React.ReactNode {
  const mainApp = row.application?.resourceGroupApplications?.primaryApplication || row.application;
  return (
    <Clickable
      onClick={() => onApplicationDashboardClick(
        row.client?.id || "",
        row.application?.id || "",
      )}
      outline="sm"
      stretch
    >
      <Table.CompanyCell
        name={
          mainApp?.resource.productName ||
          "Unknown Notification"
        }
        signedLogoUrl={
          mainApp?.resource.publicResource
            ?.signedLogoUrl || undefined
        }
      />
    </Clickable>
  );
}

export function renderNotificationType(
  value: unknown,
  row: NotificationFragment,
  onApplicationDashboardClick: (clientId: string, applicationId: string) => void,
): React.ReactNode {
  if (value === null || value === undefined) {
    return null;
  }
  return (
    <Clickable
      onClick={() => onApplicationDashboardClick(
        row.client?.id || "",
        row.application?.id || "",
      )}
      outline="sm"
      stretch
    >
      <NotificationTypeMappingText notificationType={value as NotificationEnum} />
    </Clickable>
  );
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderActions(
  _value: unknown,
  row: NotificationFragment,
  onDetailsClick: (clientId: string, applicationId: string) => void,
  onResolveClick: (id: string) => void,
): React.ReactNode {
  return (
    <Box gap={2}>
      <Dropdown
        trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
      >
        <Dropdown.Option
          label="Details"
          icon="faFileLines"
          onSelect={() =>
            onDetailsClick(
              row.client?.id || "",
              row.application?.id || "",
            )
          }
        />
        <Dropdown.Option
          label="Resolve"
          icon="faCheck"
          onSelect={() => onResolveClick(row.id)}
        />
      </Dropdown>
    </Box>
  );
}